/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, ReactNode } from 'react'
import cx from 'classnames'
import { Form, Field } from 'react-final-form'
import { Wrapper } from '@cinch-nx/shared-ui'
import { FindACarText } from '../../types/locales'
import {
  Primary,
  Text,
  TextFontSizes,
  TextFontWeights,
  TextLineHeights,
} from '@cinch-labs/design-system'
import styles from './find-a-car.module.css'
import ladyWithCarKeysLarge from './assets/lady-with-car-keys-large.png'
import ladyWithCarKeysSmall from './assets/lady-with-car-keys-small.png'
import {
  currencyValueFormatter,
  interpolateI18n,
} from '@cinch-labs/shared-util'
import {
  convertToPence,
  formatToPounds,
  getActiveCtaText,
  getFiltredUrl,
} from './find-a-car.helpers'
import FindACarRadioField from './find-a-car-radio-field/find-a-car-radio-field'
import FindACarTextField from './find-a-car-text-field/find-a-car-text-field'
import { useAnalytics } from '@cinch-nx/shared-analytics'
import { financeBudgetCalculator } from '../../tracking/tracking-events'

interface FindACarProps {
  i18n: FindACarText
}

enum FinanceType {
  PCP = 'PCP',
  HP = 'HP',
}

interface FormState {
  budgetType: FinanceType
  budget?: string
}

const calculateHpCarPrice = (monthlyBudget: number) => {
  const years = 4
  const flatInterestRate = 5.14
  const x = monthlyBudget / (1 + (years * flatInterestRate) / 100)
  return x * (years * 12) + 2000
}

function FindACar({ i18n }: FindACarProps) {
  const baseCtaUrl = '/used-cars'

  const [formData, setFormData] = useState<FormState>({
    budgetType: FinanceType.PCP,
  })
  const [ctaText, setCtaText] = useState<{
    aria: string
    short: string
    long: string
  }>({
    aria: '',
    short: '',
    long: '',
  })
  const [ctaUrl, setCtaUrl] = useState<string>(baseCtaUrl)
  const [budgetDescription, setBudgetDescription] = useState<ReactNode[]>([])
  const { trackEvent } = useAnalytics()

  const budgetTypeOptionsLabel = (
    index: keyof typeof FinanceType,
    checked: boolean,
  ) => (
    <Text size="s" fontWeight={checked ? 'bold' : 'medium'}>
      {i18n.searchSection[index].label}
    </Text>
  )
  const budgetTypeOptionsSubLabel = (
    index: keyof typeof FinanceType,
    checked: boolean,
  ) => (
    <Text
      size="s"
      className={cx(styles.budgetTypeText, {
        [styles.budgetTypeHidden]: !checked,
      })}
    >
      {i18n.searchSection[index].sublabel}
    </Text>
  )
  const budgetTypeOptions = Object.entries(FinanceType).map(([key, value]) => {
    const index = key as keyof typeof FinanceType
    return {
      value: value,
      label: (checked: boolean) => budgetTypeOptionsLabel(index, checked),
      subLabel: (checked: boolean) => budgetTypeOptionsSubLabel(index, checked),
    }
  })

  const onSubmit = (values: FormState) => {
    if (!values.budgetType) {
      return
    }
    trackFormEvents(values)
  }

  const trackFormEvents = (values: FormState) => {
    const trackValue = (() => {
      const budget = Number.parseInt(values.budget || '', 10)
      if (values.budget === '' || values.budget === undefined) {
        return '-'
      }
      if (!Number.isNaN(budget)) {
        return `${budget / 100}`
      }
      return ''
    })()
    trackEvent(financeBudgetCalculator(values.budgetType, trackValue))
  }

  useEffect(() => {
    const budget = Number.parseInt(formData.budget || '', 10)

    if (formData.budget && !Number.isNaN(budget)) {
      const budgetInPounds = budget / 100

      const isPCP = formData.budgetType === FinanceType.PCP
      const hpCarPrice: number = Math.round(calculateHpCarPrice(budgetInPounds))
      const hpCarPriceFormatted: string = currencyValueFormatter(hpCarPrice)

      const hpBudgetDescription = interpolateI18n(
        i18n.inputSection.hpBudgetDescription,
        {
          calculateHpCarPrice: hpCarPriceFormatted,
          budget: currencyValueFormatter(budgetInPounds),
        },
        {
          b: (v: string) => (
            <Text element="span" fontWeight="bold" key={v}>
              {v}
            </Text>
          ),
        },
      )

      const message = getActiveCtaText(
        i18n.cta.active.message,
        isPCP ? budgetInPounds : hpCarPrice,
      )

      if (!isPCP) {
        setBudgetDescription(hpBudgetDescription)
        setCtaUrl(getFiltredUrl(baseCtaUrl, hpCarPrice, false))
      } else {
        setCtaUrl(getFiltredUrl(baseCtaUrl, budgetInPounds, true))
      }

      setCtaText({
        aria: `${message}${isPCP ? i18n.cta.active.suffix.aria : ''}`,
        short: `${message}${isPCP ? i18n.cta.active.suffix.short : ''}`,
        long: `${message}${isPCP ? i18n.cta.active.suffix.long : ''}`,
      })
    } else {
      setCtaText({
        aria: i18n.cta.default,
        short: i18n.cta.default,
        long: i18n.cta.default,
      })
      setCtaUrl(baseCtaUrl)
      setBudgetDescription([])
    }
  }, [formData.budget, formData.budgetType])

  return (
    <Wrapper>
      <article className={styles.findACar}>
        <Text
          element="h2"
          className={styles.financeFeatureTitle}
          fontSize={TextFontSizes['4xl']}
          fontWeight={TextFontWeights.bold}
          lineHeight={TextLineHeights.display}
        >
          {i18n.title}
        </Text>
        <picture className={styles.financeFeaturePicture}>
          <source
            srcSet={`${ladyWithCarKeysSmall.src} 1x, ${ladyWithCarKeysSmall.src} 2x`}
            type="image/png"
            media="(max-width: 767px)"
          />
          <img
            className={styles.financeFeatureImage}
            srcSet={`${ladyWithCarKeysLarge.src} 1x, ${ladyWithCarKeysLarge.src} 2x`}
            alt={i18n.alt}
            loading="lazy"
          />
        </picture>
        <Form
          initialValues={formData}
          onSubmit={onSubmit}
          render={({ handleSubmit, values }) => (
            <form className={styles.financeFeatureForm} onSubmit={handleSubmit}>
              <Field
                name="budgetType"
                component={FindACarRadioField}
                options={budgetTypeOptions}
                label={i18n.searchSection.subtitle}
                classNames={{
                  container: styles.budgetTypeOptionsContainer,
                  legend: styles.budgetTypeOptionLegend,
                  radio: {
                    container: styles.budgetTypeOptionRadioContainer,
                    label: styles.budgetTypeOptionRadioLabel,
                  },
                }}
                onChange={(evt: any) => {
                  setFormData({ ...formData, budgetType: evt.target.value })
                }}
              />

              <Field
                id="budgetInput"
                name="budget"
                component={FindACarTextField}
                label={i18n.inputSection.subtitle}
                placeholder={`£`}
                format={formatToPounds}
                inputMode="numeric"
                fullWidth
                classNames={{
                  container: styles.searchInputContainer,
                  label: { label: styles.searchInputLabel },
                }}
                maxLength={5}
                onChange={(evt: any) => {
                  setFormData({
                    ...formData,
                    budget: convertToPence(evt.target.value),
                  })
                }}
              />
              {budgetDescription.length > 0 &&
                formData.budgetType === FinanceType.HP && (
                  <Text
                    fontWeight="book"
                    fontSize={'m'}
                    className={styles.budgetDescription}
                  >
                    {budgetDescription}
                  </Text>
                )}
              <Text
                className={styles.searchDescription}
                fontWeight="book"
                fontSize="xs"
              >
                {i18n.inputSection.description}
              </Text>
              <Primary
                isFullWidth={true}
                element={'a'}
                href={ctaUrl}
                aria-label={ctaText.aria}
                onClick={() => trackFormEvents(values)}
              >
                <span
                  data-testid="ctaTextShort"
                  className={styles.searchCtaShort}
                >{`${ctaText.short}`}</span>
                <span
                  data-testid="ctaTextLong"
                  className={styles.searchCtaLong}
                >{`${ctaText.long}`}</span>
              </Primary>
            </form>
          )}
        />
      </article>
    </Wrapper>
  )
}

export default FindACar
